// The fleet row of FLEET · MISSIONS (R1816; mockup round 085 card 07 as the
// owner amended it on 2026-10-09; R1844 after the owner's test of the same
// day; ui-map-interface.md §12 "FLEET · MISSIONS").
//
// One row for every hull, at sea or in the pens, combat or support: 44 px
// with two lines and one column set.
//
// - Line 1: the hull mark of the class, the CLASS in the cap step and the
//   dim ink, then the name in the body step and the owner ink, at sea and
//   in the pens: two steps and two inks part the two words.
// - Line 2 of a hull at sea: the order, HULL and SHIELD as a bar and a
//   percent each, then the one load the class keeps: the tubes of a
//   torpedo hull or the rounds of a stowing hull. Full tubes and an empty hold state nothing. The cargo of a Dredger
//   stands on line 1 after the name.
// - Line 2 of a hull in the pens: DOCKED, HULL as a bar and a percent, the
//   fit strip of its systems (R1468), and what it is prepared with. A
//   whole hull (100%) states what it is prepared with alone, the captain.
//
// A fleet is one hull, so no row states a count. A press on the row of a
// hull at sea selects it and centres the map: one gesture with one
// outcome. Every order stands in ⋯ of the row and on the card. A hull in
// the pens has no place on the map, so its row carries the one press of
// the step that blocks its deploy (`prepStep` of `deployask.ts`, R1888): CAPTAIN for a
// warship with no officer, a dim UNDER REPAIR while a repair of the hull
// is in the queue, else DEPLOY. Its ⋯ holds START MISSION, CAPTAIN,
// LOADOUT, REPAIR and BREAK: the pens live in FLEET, and the DRAFT tab holds
// the library alone (R1888, which supersedes the split of R84).
//
// The module is pure: it turns the state into a `FleetRow` and the row
// into markup. It holds no field of the overlay.

import type { ShipSpec, ShipStock } from '../gen/proto/aquaphobia';
import { AnchorState, Building, JobKind } from '../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../gen/strings';
import type { SceneLook } from '../render/scene-look';
import { costText, dur, num } from '../state';
import { RESOURCES } from '../gen/balance';
import { techIcon } from './buildicons';
import { buttonHtml, type Action, type CardEnv } from './cards/card';
import { penStep } from './deployask';
import { orderAction, type MapCardCtx } from './cards/mapcards';
import { shipMarkSvg } from './cards/marks';
import { fleetOrderGate, orderRow } from './cards/orders';
import { esc } from './esc';
import { anchorLabel, cssColor, fillPct, holdTotal, hullBand, pctFig, ratioFig } from './format';
import { hullMagazine, kindsAboard, loadableKinds, mixText } from './rounds';
import { fitStrip, missionLabel, prepLine } from './overlay/markup';
import type { UIFleet } from './overlay/types';
import { stowsAtPens } from './overlay/stowsheet';

/** One bar of line 2: the fill, 0 to 1. */
export interface RowBar {
  frac: number;
  /** The hull is in the bad band: the bar and the percent wear the bad ink. */
  low?: boolean;
  /** The hull is in the middle band: the bar and the percent wear the amber ink. */
  mid?: boolean;
}

/** One row of the fleet list. Every string is plain text but the markup
 *  fields `mark`, `load`, `systems` and `prep`. */
export interface FleetRow {
  id: number;
  docked: boolean;
  /** The hull mark of the class, as markup (`shipMarkSvg`). */
  mark: string;
  /** The class name, as the catalog gives it. */
  cls: string;
  name: string;
  /** The owner ink of the name, at sea and in the pens. */
  ink: string;
  /** The control group slot the hull holds, or 0. */
  group: number;
  /** The order word: the order at sea, DOCKED in the pens. */
  order: string;
  /** The last reason the hull stopped or turned (R3265), or '': the words
   *  of the newest notice that named it. It stands in place of the order
   *  word, and the whole notice is its title. */
  why?: string;
  hull: RowBar;
  /** The shield of a hull at sea; a hull in the pens states none. */
  shield?: RowBar;
  /** The load part of a hull at sea (`loadPart`), or ''. */
  load: string;
  /** The cargo part of a hull at sea (`cargoPart`), as markup, or ''. It stands after the name. */
  cargo: string;
  /** The fit strip of a hull in the pens, or ''. */
  systems: string;
  /** What a hull in the pens is prepared with (`prepLine`), or ''. */
  prep: string;
  /** The one press of a row in the pens. */
  press?: Action;
  /** The wrench beside the press of a worn hull in the pens: the repair
   *  in one press (R1906). Its full twin, with the cost, stands in ⋯. */
  quick?: Action;
  /** The presses of ⋯. */
  more: Action[];
  moreOpen: boolean;
  selected: boolean;
}

/**
 * The load part of line 2: the tubes of a torpedo hull, the rounds of a
 * hull that stows them (R712), or ''. A mixed load states its mix in the
 * short form, `tubes 6H · 3R`, full or not (R2031); one kind states the
 * ratio, and full tubes of one kind state nothing.
 */
export function loadPart(f: Pick<UIFleet, 'torpedoes' | 'rounds'>,
  spec: ShipSpec | undefined, magazine: number, nameOf: (kind: number) => string): string {
  const armed = (spec?.torpedoes.length ?? 0) > 0;
  if (armed || (spec?.stows.length ?? 0) > 0) {
    const load = f.rounds ?? [];
    if (armed && kindsAboard(load) > 1) return S.fl_tubes({ fig: mixText(load, nameOf, true) });
    if (armed && magazine > 0 && f.torpedoes >= magazine) return '';
    const fig = magazine > 0 ? ratioFig(f.torpedoes, magazine) : num(f.torpedoes);
    return armed ? S.fl_tubes({ fig }) : S.fl_rounds({ fig });
  }
  return '';
}

/** The cargo of a hull with a hold, or '' while the hold is empty. It stands on line 1 after the
 *  name, because line 2 has no room for it. */
export function cargoPart(f: Pick<UIFleet, 'cargoCap' | 'hold'>): string {
  if (f.cargoCap <= 0) return '';
  const held = holdTotal(f.hold);
  return held > 0 ? S.fl_cargo({ fig: pctFig(held / f.cargoCap * 100) }) : '';
}

/** The hull bar of a row: the fill and the ink of its wear (R1906). */
function hullBar(pct: number): RowBar {
  // Any wear shows: amber while the hull holds, bad in the bad band.
  const bad = hullBand(pct) === 'bad';
  return { frac: pct / 100, low: bad, mid: !bad && pct < 100 };
}

/** A cost as plain words, for a hover: `120 alloy · 40 biomass`. */
function costPlain(c: ShipStock['repairCost']): string {
  if (!c) return '';
  const parts: string[] = [];
  for (const r of RESOURCES) {
    const v = c[r.key as keyof typeof c];
    if (v) parts.push(`${num(v)} ${r.name.toLowerCase()}`);
  }
  return parts.join(' · ') || '—';
}

/**
 * The reason part of line 2 (R3265): the words of the notice without the
 * name of the hull it starts with, because line 1 names the hull. A
 * notice that does not start with the name stands whole.
 */
export function whyPart(text: string, name: string): string {
  if (!text || !name || !text.startsWith(name)) return text;
  return text.slice(name.length).replace(/^[\s:·—–-]+/, '') || text;
}

/** The order word of line 2: the anchor cycle while it runs, else the mission. */
function orderWord(ctx: MapCardCtx, f: UIFleet): string {
  if (f.anchorLeftSec > 0 && f.anchor !== AnchorState.ANCHOR_STATE_UNSPECIFIED) return anchorLabel(f, ctx.s);
  return missionLabel(f);
}

/** What a row reads of the screen beside the state: the reader's look,
 *  whose ship icon set draws the hull mark. */
export interface RowOpts {
  look?: SceneLook;
}

/**
 * The row of a hull at sea. ⋯ holds the whole order row of the card in
 * its order (R1809): the action press, the quiet order, then the rest.
 */
export function seaRow(ctx: MapCardCtx, f: UIFleet,
  o: RowOpts & { moreOpen: boolean; selected: boolean }): FleetRow {
  const s = ctx.s;
  const spec = s.ship(f.cls);
  const row = orderRow(f.cls, spec?.dps ?? 0, fleetOrderGate(s, f.cls, spec));
  return {
    id: f.id, docked: false, mark: shipMarkSvg(f.cls, o.look), cls: spec?.name ?? '?', name: f.name,
    ink: cssColor(s.faction), group: ctx.groupOf(f.id), order: orderWord(ctx, f), why: s.fleetWhy(f.id),
    hull: hullBar(f.hullPct),
    shield: { frac: f.shieldPct / 100 },
    load: loadPart(f, spec, hullMagazine(spec, f.firingKind, (k) => s.torpedo(k)), (k) => s.torpedo(k)?.name ?? '?'),
    cargo: cargoPart(f),
    systems: '', prep: '',
    more: [
      orderAction(ctx, f, row.action, 'dark'),
      ...row.quiet.map((x) => orderAction(ctx, f, x, 'quiet')),
      ...row.more.map((x) => orderAction(ctx, f, x, 'quiet')),
    ],
    moreOpen: o.moreOpen, selected: o.selected,
  };
}

/** What the row of a hull in the pens reads of the overlay beside the
 *  state: the open ⋯, the armed target pick, the open choice under the
 *  row and the armed BREAK (R696). */
export interface DockedOpts extends RowOpts {
  moreOpen: boolean;
  picking: boolean;
  /** The picker card that stands open for this row: the captain or the
   *  LOADOUT sheet (R2031), or none. */
  dialog?: '' | 'cap' | 'load' | 'stow';
  /** The first press of BREAK armed it; the press shows BREAK UP? (R2069). */
  breakArmed?: boolean;
  /** The hull is the selection: its card is the deploy ask. */
  selected?: boolean;
}

/**
 * The row of a hull in the pens (R1888). The press is the step that
 * blocks the deploy (`prepStep`). ⋯ holds START MISSION while nothing
 * blocks the deploy, lit while its target pick waits; CAPTAIN for a
 * warship (a support hull must not carry one, R385); LOADOUT for a hull that
 * loads two or more round kinds, which opens the LOADOUT sheet (R2031); a
 * hull of one kind has no LOADOUT, and line 2 states its load; REPAIR with
 * its cost, off while the hull is whole, while no
 * Shipyard stands or while the repair is in the queue; and BREAK, armed
 * for a second press (R696).
 */
export function dockedRow(ctx: MapCardCtx, st: ShipStock, o: DockedOpts): FleetRow {
  const s = ctx.s;
  const spec = s.ship(st.cls);
  const support = spec?.support ?? false;
  const cap = s.captain(st.captainId);
  const repairing = s.underRepair(st.fleetId);
  const step = penStep(s, st);
  const left = 100 - st.wearPct;
  const mixes = loadableKinds(spec, s.catalog?.torpedoes ?? []).length > 1;
  const wharf = s.level(Building.BUILDING_WHARF) > 0;
  const capPress: Action = {
    act: 'capdlg', id: st.fleetId, label: S.btn_captain, face: 'quiet', on: o.dialog === 'cap',
  };
  let press: Action;
  switch (step) {
    case 'repairing':
      press = { act: 'deployd', id: st.fleetId, label: S.deploy_status_repairing, face: 'quiet', off: true };
      break;
    case 'captain':
      press = { ...capPress, face: 'dark' };
      break;
    default:
      press = { act: 'deployd', id: st.fleetId, n: 0, label: S.btn_deploy, face: 'dark' };
  }
  const worn = st.wearPct > 0;
  const repairTime = worn ? dur(s.buildSec(JobKind.JOB_KIND_REPAIR, st.repairTimeSec)) : '';
  // The wrench: a worn hull repairs in one press, beside its deploy press.
  const quick: Action | undefined = worn && !repairing ? {
    act: 'repair', id: st.fleetId, label: S.btn_repair, labelHtml: techIcon('R2'), face: 'quiet',
    off: !wharf,
    title: S.fl_quick_repair_title({ cost: costPlain(st.repairCost), time: repairTime }),
  } : undefined;
  const more: Action[] = [];
  if (step === 'deploy') {
    more.push({
      act: 'deploystart', id: st.fleetId, n: 0, face: 'quiet', on: o.picking,
      label: o.picking ? S.btn_pick_target : S.fl_start_mission,
    });
  }
  if (!support) more.push(capPress);
  if (mixes) {
    more.push({ act: 'loaddlg', id: st.fleetId, label: S.btn_load, face: 'quiet', on: o.dialog === 'load' });
  }
  // STOW of a Sapper while `sapper` holds (R2654, R2658): it opens the
  // STOW sheet, which sends `FLEET_OP_SET_STOW`.
  if (stowsAtPens(s, st)) {
    more.push({ act: 'stowdlg', id: st.fleetId, label: S.btn_stow, face: 'quiet', on: o.dialog === 'stow' });
  }
  more.push({
    act: 'repair', id: st.fleetId, label: S.btn_repair, face: 'quiet',
    off: !worn || !wharf || repairing,
    costHtml: worn ? costText(st.repairCost) : undefined,
    title: worn ? S.fl_repair_title({ time: repairTime }) : undefined,
  });
  more.push({
    act: 'breakfleet', id: st.fleetId, face: 'quiet', arm: true, danger: true, armed: o.breakArmed,
    label: S.btn_break, armLabel: S.break_armed,
  });
  return {
    id: st.fleetId, docked: true, mark: shipMarkSvg(st.cls, o.look), cls: spec?.name ?? '?', name: st.name,
    ink: cssColor(s.faction), group: ctx.groupOf(st.fleetId), order: S.fl_docked, why: s.fleetWhy(st.fleetId),
    hull: hullBar(left),
    load: '', cargo: '', systems: fitStrip(s.catalog, st), prep: prepLine(s, st, support, cap),
    press, quick, more,
    moreOpen: o.moreOpen, selected: o.selected ?? false,
  };
}

/** One bar of line 2: the label, a short bar and the percent. The fill
 *  rides the custom property `--fl-hull` or `--fl-shield` of the row. */
function barPart(label: string, b: RowBar, kind: 'hull' | 'shield'): string {
  return `<span class="fl-v ${kind}${b.low ? ' low' : b.mid ? ' mid' : ''}">${esc(label)}`
    + `<i class="fl-vb"><b></b></i>${pctFig(b.frac * 100)}</span>`;
}

/** The dot between two parts of the second line of a row. The expedition
 *  rows and the war rows write their second line with it too. */
export const FL_SEP = '<i class="fl-sep">·</i>';

/** One row as markup, and the grid of ⋯ under it while it is open. */
export function fleetRowHtml(r: FleetRow, env: CardEnv): string {
  const cls = ['fl-row', r.docked ? 'dock' : 'sea'];
  if (r.selected) cls.push('sel');
  if (r.moreOpen && r.more.length > 0) cls.push('open');
  // The figures of the row are data: the owner ink and the two fills.
  const vars = `--fl-hull:${fillPct(r.hull.frac)}`
    + (r.shield ? `;--fl-shield:${fillPct(r.shield.frac)}` : '')
    + (r.ink ? `;--fl-ink:${r.ink}` : '');
  // A hull in the pens with a whole hull states the captain alone.
  const whole = r.docked && r.hull.frac >= 1;
  // The last reason stands in place of the order word (R3265), cut at
  // the end of the line, with the whole notice as its title.
  const why = r.why ? `<span class="ord why" title="${esc(r.why)}">${esc(whyPart(r.why, r.name))}</span>` : '';
  const line2 = whole ? [why, r.prep].filter(Boolean) : [
    why || `<span class="ord">${esc(r.order)}</span>`,
    barPart(CS.v_hull, r.hull, 'hull'),
    ...(r.shield ? [barPart(CS.v_shield, r.shield, 'shield')] : []),
    ...[r.load, r.systems, r.prep].filter(Boolean),
  ];
  const inner = `<span class="fl-mk">${r.mark}</span>`
    + `<span class="fl-l1"><b class="fl-cls">${esc(r.cls)}</b><span class="fl-nm">${esc(r.name)}</span>`
    + (r.cargo ? `<span class="fl-cg">${r.cargo}</span>` : '') + '</span>'
    + `<span class="fl-l2">${line2.join(FL_SEP)}</span>`;
  // The row is the press: it selects the hull. A hull at sea centres the
  // map; a hull in the pens is not on the map, so its card is the deploy
  // ask, as the digit of its control group shows it (R1988).
  const title = r.docked ? S.fl_dock_row_title({ name: r.name }) : S.fl_row_title({ name: r.name });
  const main = `<button type="button" class="fl-main" data-act="flsel" data-id="${r.id}" title="${esc(title)}">${inner}</button>`;
  // No group: the column stays, empty.
  const grp = r.group > 0
    ? `<span class="fl-grp on" title="${esc(S.fl_group_title({ n: r.group }))}">${r.group}</span>`
    : '<span class="fl-grp"></span>';
  const press = r.press
    ? `<span class="fl-press">${r.quick ? buttonHtml(r.quick, env).replace('class="ck-b', 'class="ck-b fl-quick') : ''}`
      + `${buttonHtml(r.press, env)}</span>` : '';
  // A row with a press and nothing in ⋯ keeps no empty ⋯ column, so the
  // press stands at the right edge.
  const more = r.more.length > 0
    ? `<button type="button" class="fl-more${r.moreOpen ? ' on' : ''}" data-act="flmore" data-id="${r.id}"`
      + ` title="${esc(S.fl_more_title({ name: r.name }))}" aria-expanded="${r.moreOpen}">${CS.more_mark}</button>`
    : r.press ? '' : '<span class="fl-more"></span>';
  const grid = r.moreOpen && r.more.length > 0
    ? `<div class="fl-grid">${r.more.map((a) => buttonHtml(a, env)).join('')}</div>` : '';
  return `<div class="${cls.join(' ')}" style="${vars}">${main}${grp}${press}${more}</div>${grid}`;
}

/**
 * A row of the list that is no hull on the map: a running dive, a dive in
 * the build queue, a pledged fleet (R680, R733, R738). It keeps the two
 * lines and the columns of a fleet row, with no mark and no group.
 */
export function noteRowHtml(name: string, kind: string, sub: string, press = '', cls = ''): string {
  return `<div class="fl-row note${cls ? ` ${cls}` : ''}"><span class="fl-main"><span class="fl-mk"></span>`
    + `<span class="fl-l1"><b class="fl-cls">${esc(kind)}</b><span class="fl-nm">${esc(name)}</span></span>`
    + `<span class="fl-l2">${sub}</span></span><span class="fl-grp"></span>`
    + (press ? `<span class="fl-press">${press}</span>` : '')
    + '<span class="fl-more"></span></div>';
}
